import { useEffect, useRef } from 'react'
import { TenantLogo } from '@/components/tenant-logo'
import type { LoyaltyCard as LoyaltyCardType } from '@/api/store-loyalty-card-types'
import { useCustomer } from '@/providers/auth-provider'
import { useI18n } from '@/i18n/i18n-react'
import { tenant } from '@/tenants/tenant'
import { precisionAmount } from '@/utils/currency'

interface LoyaltyCardProps {
	card: LoyaltyCardType
}

export function LoyaltyCard({ card }: LoyaltyCardProps) {
	const { L } = useI18n()
	const customer = useCustomer()
	const { features } = tenant
	const canvasRef = useRef<HTMLCanvasElement>(null)
	const barcodeValue = (card.uniqueNumber?.trim() ?? card.cardNumber?.trim() ?? '').replace(/\s/g, '')

	useEffect(() => {
		if (!canvasRef.current || !barcodeValue) return

		// Dynamically import bwip-js to keep initial bundle lean
		import('bwip-js').then(({ default: bwipjs }) => {
			try {
				bwipjs.toCanvas(canvasRef.current!, {
					bcid: 'pdf417',
					text: barcodeValue,
					scale: 2,
					height: 12,
					includetext: false
				})
			} catch {
				// non-critical — barcode just won't render
			}
		})
	}, [barcodeValue])

	return (
		<div className='w-full' style={{ aspectRatio: '1.586' }}>
			<div className='bg-primary relative flex h-full w-full flex-col justify-between overflow-hidden rounded-3xl p-5'>
				{/* Header row: name + logo */}
				<div className='flex items-start justify-between'>
					<div>
						<p className='text-primary-foreground text-lg font-semibold'>{customer.fullName}</p>
						<p className='text-primary-foreground/70 text-sm'>{L.LoyaltyCard.CardNumber()}</p>
						<p className='text-primary-foreground font-mono text-sm'>{card.cardNumber}</p>
					</div>
					<TenantLogo asset={tenant.logoSmall} className='text-primary-foreground h-8 w-auto' />
				</div>

				{/* Points */}
				<div className='mt-auto'>
					<p className='text-primary-foreground/70 text-xs'>{L.LoyaltyCard.AvailablePoints()}</p>
					<p className='text-primary-foreground text-3xl font-bold'>
						{L.Amount.Default({
							amount: precisionAmount(card.availablePoints),
							currencyCode: card.currencyCode
						})}
					</p>
				</div>

				{/* Barcode */}
				{barcodeValue && (
					<div className='mt-3 flex justify-end'>
						<div className='rounded bg-white p-1'>
							<canvas ref={canvasRef} />
						</div>
					</div>
				)}
			</div>
		</div>
	)
}
